import { Preferences } from '@capacitor/preferences';
import { CheckAgeSignalsResult } from 'insight-age-check';

import {
  createSimulatedAgeSignal,
  SimulatedAgeSignalScenario,
} from './ageSignalBehavior';

export enum AgeGateStartupDebugMode {
  Auto = 'AUTO',
  BypassAllow = 'BYPASS_ALLOW',
  SimulateVerifiedAdult = 'SIMULATE_VERIFIED_ADULT',
  SimulateApprovalPending = 'SIMULATE_APPROVAL_PENDING',
  SimulateApprovalDenied = 'SIMULATE_APPROVAL_DENIED',
}

export interface AgeGateStartupDebugOption {
  value: AgeGateStartupDebugMode;
  label: string;
  description: string;
}

export const ageGateStartupDebugPreferenceKey = 'ageGateStartupDebugMode';

export const ageGateStartupDebugOptions: AgeGateStartupDebugOption[] = [
  {
    value: AgeGateStartupDebugMode.Auto,
    label: 'Auto',
    description: 'Use the normal launch flow, with automatic bypass only when the runtime environment is unsupported for live testing.',
  },
  {
    value: AgeGateStartupDebugMode.BypassAllow,
    label: 'Bypass and allow',
    description: 'Skip age verification entirely and continue straight into the app on the next launch.',
  },
  {
    value: AgeGateStartupDebugMode.SimulateVerifiedAdult,
    label: 'Simulate allowed',
    description: 'Pretend the startup check returned an allowed adult signal and continue into the app.',
  },
  {
    value: AgeGateStartupDebugMode.SimulateApprovalPending,
    label: 'Simulate blocked pending',
    description: 'Pretend the startup check returned a supervised approval pending result and block launch.',
  },
  {
    value: AgeGateStartupDebugMode.SimulateApprovalDenied,
    label: 'Simulate blocked denied',
    description: 'Pretend the startup check returned a supervised approval denied result and block launch.',
  },
];

export const parseAgeGateStartupDebugMode = (
  value?: string | null,
): AgeGateStartupDebugMode => {
  switch (value) {
    case AgeGateStartupDebugMode.BypassAllow:
      return AgeGateStartupDebugMode.BypassAllow;
    case AgeGateStartupDebugMode.SimulateVerifiedAdult:
      return AgeGateStartupDebugMode.SimulateVerifiedAdult;
    case AgeGateStartupDebugMode.SimulateApprovalPending:
      return AgeGateStartupDebugMode.SimulateApprovalPending;
    case AgeGateStartupDebugMode.SimulateApprovalDenied:
      return AgeGateStartupDebugMode.SimulateApprovalDenied;
    default:
      return AgeGateStartupDebugMode.Auto;
  }
};

export const getStoredAgeGateStartupDebugMode = async () => {
  const { value } = await Preferences.get({
    key: ageGateStartupDebugPreferenceKey,
  });

  return parseAgeGateStartupDebugMode(value);
};

export const setStoredAgeGateStartupDebugMode = async (
  mode: AgeGateStartupDebugMode,
) => {
  await Preferences.set({
    key: ageGateStartupDebugPreferenceKey,
    value: mode,
  });
};

export const getAgeGateStartupDebugResult = (
  mode: AgeGateStartupDebugMode,
): CheckAgeSignalsResult | null => {
  switch (mode) {
    case AgeGateStartupDebugMode.SimulateVerifiedAdult:
      return createSimulatedAgeSignal(SimulatedAgeSignalScenario.VerifiedAdult);
    case AgeGateStartupDebugMode.SimulateApprovalPending:
      return createSimulatedAgeSignal(SimulatedAgeSignalScenario.SupervisedApprovalPending);
    case AgeGateStartupDebugMode.SimulateApprovalDenied:
      return createSimulatedAgeSignal(SimulatedAgeSignalScenario.SupervisedApprovalDenied);
    default:
      return null;
  }
};
